import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import {
  getCustomerMembers,
  removeCustomerMember,
  updateUserRoleByWorkspaceId,
} from '../thunks/users';
import { fetchSituationUsers } from '../thunks/monitoring';

const usersAdapter = createEntityAdapter({});

export const usersSlice = createSlice({
  name: 'users',
  initialState: usersAdapter.getInitialState({}),
  reducers: {
    setUsers: (state, action) => {
      usersAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerMembers.fulfilled, (state, action) => {
        usersAdapter.upsertMany(state, action.payload);
      })
      .addCase(removeCustomerMember.fulfilled, (state, action) => {
        const { userId, customerId } = action.meta.arg;

        const user = state.entities[userId];
        if (!user) return;

        // Server deletes both the member's customer role and their device roles
        // for that customer, so drop roles tied to it by either path — matching
        // getUsersWithCustomerMembership. customerId is the scalar; customer the
        // nested object — GET_CUSTOMER_MEMBERS only fetches the scalar, so without
        // the fallback a customer-wide member's role was never matched here and
        // stayed in the table until the next full refetch.
        const newUserRoles = user.userRoles.filter(
          (role) =>
            (role.customer?.id ?? role.customerId) !== customerId &&
            role.device?.customerId !== customerId
        );

        if (newUserRoles.length === 0) {
          usersAdapter.removeOne(state, userId);
        } else {
          usersAdapter.updateOne(state, {
            id: userId,
            changes: {
              userRoles: newUserRoles,
            },
          });
        }
      })
      .addCase(fetchSituationUsers.fulfilled, (state, action) => {
        usersAdapter.upsertMany(state, action.payload);
      })
      .addCase(updateUserRoleByWorkspaceId.fulfilled, (state, action) => {
        const newRole = action.payload;
        const userId = action.meta.arg.userId;

        usersAdapter.updateOne(state, {
          id: userId,
          changes: {
            userRoles: state.entities[userId].userRoles.map((role) =>
              role.id === newRole.id ? newRole : role
            ),
          },
        });
      });
  },
});

export const { setUsers } = usersSlice.actions;

export const usersAdapterSelectors = usersAdapter.getSelectors(
  (state) => state.users
);

export default usersSlice.reducer;
